import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  Stack,
  useMediaQuery
} from '@mui/material';
import { ThemeProvider, alpha, SxProps, Theme, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarIcon,
  ViewList as ListIcon,
  ChevronLeft,
  ChevronRight,
  DragIndicator as DragIndicatorIcon,
  GridView as GridViewIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lt } from 'date-fns/locale';
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addMonths,
  addWeeks,
  addDays,
  subMonths,
  subWeeks,
  subDays
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import webSocketService from '../services/WebSocketService';
import { workPlansNestedTheme } from './workPlans/workPlansTheme';
import {
  WORK_PLANS_ACCENT,
  getPriorityToken,
  getStatusToken,
  CARD_PROJECT,
  CARD_REGULAR,
  PROJECT_BADGE_SX,
  ROW_HOVER_BG,
  ROW_NEW_BG,
  ROW_SELECTED_BG,
  WEEKDAY_HEADERS_LT
} from './workPlans/workPlansTokens';
import { WorkPlansStatusChip } from './workPlans/WorkPlansStatusChip';
import { WorkPlansPriorityChip } from './workPlans/WorkPlansPriorityChip';

type WorkPlanSortableDragProps = Pick<ReturnType<typeof useSortable>, 'attributes' | 'listeners'>;

interface WorkPlan {
  id: string;
  userId: string;
  category: string;
  taskName: string;
  date: string;
  priority: 'low' | 'medium' | 'high' | 'extreme' | 'ultra';
  status: 'laukia' | 'pradėta' | 'baigta' | 'atšaukta' | 'perduota' | 'grąžinta';
  difficulty: number | null;
  delegate: string | null;
  order: number | null;
  createdAt: string;
  updatedAt: string;
  // Projektai task fields
  taskId?: string;
  isProjectTask?: boolean;
  project?: {
    id: string;
    title: string;
  };
  description?: string;
  dueDate?: string;
}

const WORK_PLAN_STATUSES: WorkPlan['status'][] = ['laukia', 'pradėta', 'baigta', 'atšaukta', 'perduota', 'grąžinta'];
const WORK_PLAN_PRIORITIES: WorkPlan['priority'][] = ['low', 'medium', 'high', 'extreme', 'ultra'];

/** Shared class for EditableCell wrappers (table + mobile stack) so click-outside detection works */
const WORK_PLAN_EDITABLE_FIELD_CLASS = 'work-plan-editable-field';

/** Stable wrapper for mobile list rows — only handles @dnd-kit sortable + outer chrome */
interface SortableMobileCardFrameProps {
  plan: WorkPlan;
  isSelected: boolean;
  isNew: boolean;
  editingLocked: boolean;
  onRowClick: () => void;
  children: (drag: WorkPlanSortableDragProps) => React.ReactNode;
}

const SortableMobileCardFrame: React.FC<SortableMobileCardFrameProps> = ({
  plan,
  isSelected,
  isNew,
  editingLocked,
  onRowClick,
  children,
}) => {
  const cardBg = plan.isProjectTask
    ? isSelected
      ? ROW_SELECTED_BG
      : isNew
      ? ROW_NEW_BG
      : CARD_PROJECT.bg
    : isSelected
    ? ROW_SELECTED_BG
    : isNew
    ? ROW_NEW_BG
    : '#ffffff';

  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: plan.id,
    disabled: plan.isProjectTask || editingLocked,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: plan.isProjectTask || editingLocked ? 'default' : 'grab',
    ...(isDragging ? { zIndex: 1000 } : {}),
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={onRowClick}
      sx={{
        border: plan.isProjectTask ? '1.5px solid #C7D2FE' : '1px solid #F0F4F8',
        borderRadius: 2,
        p: 1,
        bgcolor: cardBg,
        '&:hover': { bgcolor: editingLocked ? undefined : ROW_HOVER_BG },
        cursor: plan.isProjectTask ? 'pointer' : editingLocked ? 'pointer' : 'grab',
        ...(isDragging && {
          boxShadow: 3,
          bgcolor: ROW_SELECTED_BG,
        }),
      }}
    >
      {children({ attributes, listeners })}
    </Box>
  );
};

function computeTaskStats(plans: WorkPlan[]): {
  statusCounts: Record<WorkPlan['status'], number>;
  priorityCounts: Record<WorkPlan['priority'], number>;
  averageDifficulty: number | null;
} {
  const statusCounts = Object.fromEntries(WORK_PLAN_STATUSES.map((s) => [s, 0])) as Record<
    WorkPlan['status'],
    number
  >;
  const priorityCounts = Object.fromEntries(WORK_PLAN_PRIORITIES.map((p) => [p, 0])) as Record<
    WorkPlan['priority'],
    number
  >;

  let difficultySum = 0;
  let difficultyCount = 0;

  for (const plan of plans) {
    if (WORK_PLAN_STATUSES.includes(plan.status)) {
      statusCounts[plan.status]++;
    }
    if (WORK_PLAN_PRIORITIES.includes(plan.priority)) {
      priorityCounts[plan.priority]++;
    }
    if (plan.difficulty !== null && plan.difficulty !== undefined) {
      const n = Number(plan.difficulty);
      if (!Number.isNaN(n)) {
        difficultySum += n;
        difficultyCount += 1;
      }
    }
  }

  return {
    statusCounts,
    priorityCounts,
    averageDifficulty: difficultyCount > 0 ? difficultySum / difficultyCount : null,
  };
}

const WorkPlans: React.FC = () => {
  const { user, token } = useAuth();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobileListLayout = useMediaQuery(theme.breakpoints.down('md'));

  const getCategoryLabel = (category: string) => {
    if (!category) return '';
    const key = `workPlans.categories.${category}`;
    return i18n.exists(key) ? t(key) : category;
  };

  // Helper functions to read initial state from URL params and localStorage
  const getInitialDisplayMode = (): 'calendar' | 'list' => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('workPlansDisplayMode');
      if (fromQuery === 'calendar' || fromQuery === 'list') {
        return fromQuery;
      }
      const fromStorage = window.localStorage.getItem('workPlansDisplayMode');
      if (fromStorage === 'calendar' || fromStorage === 'list') {
        return fromStorage;
      }
    } catch {}
    return 'calendar';
  };

  const getInitialStatusFilters = (): Set<string> => {
    const validStatuses = ['laukia', 'pradėta', 'baigta', 'atšaukta', 'perduota', 'grąžinta'];
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('workPlansStatuses');
      if (fromQuery) {
        const statuses = fromQuery.split(',').filter(s => validStatuses.includes(s.trim()));
        if (statuses.length > 0) {
          return new Set(statuses);
        }
      }
      const fromStorage = window.localStorage.getItem('workPlansStatuses');
      if (fromStorage) {
        try {
          const parsed = JSON.parse(fromStorage);
          if (Array.isArray(parsed)) {
            const statuses = parsed.filter(s => validStatuses.includes(s));
            if (statuses.length > 0) {
              return new Set(statuses);
            }
          }
        } catch {}
      }
    } catch {}
    return new Set();
  };

  const getInitialPriorityFilters = (): Set<string> => {
    const validPriorities = ['low', 'medium', 'high', 'extreme', 'ultra'];
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('workPlansPriorities');
      if (fromQuery) {
        const priorities = fromQuery.split(',').filter(p => validPriorities.includes(p.trim()));
        if (priorities.length > 0) {
          return new Set(priorities);
        }
      }
      const fromStorage = window.localStorage.getItem('workPlansPriorities');
      if (fromStorage) {
        try {
          const parsed = JSON.parse(fromStorage);
          if (Array.isArray(parsed)) {
            const priorities = parsed.filter(p => validPriorities.includes(p));
            if (priorities.length > 0) {
              return new Set(priorities);
            }
          }
        } catch {}
      }
    } catch {}
    return new Set();
  };

  const getInitialViewType = (): 'daily' | 'weekly' | 'monthly' => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('workPlansPeriod');
      if (fromQuery === 'daily' || fromQuery === 'weekly' || fromQuery === 'monthly') {
        return fromQuery;
      }
      const fromStorage = window.localStorage.getItem('workPlansPeriod');
      if (fromStorage === 'daily' || fromStorage === 'weekly' || fromStorage === 'monthly') {
        return fromStorage;
      }
    } catch {}
    return 'daily';
  };

  const getInitialSelectedDate = (): Date => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('workPlansDate');
      if (fromQuery && /^\d{4}-\d{2}-\d{2}$/.test(fromQuery)) {
        const parsed = parseISO(fromQuery);
        if (!Number.isNaN(parsed.getTime())) return parsed;
      }
      const fromStorage = window.localStorage.getItem('workPlansDate');
      if (fromStorage && /^\d{4}-\d{2}-\d{2}$/.test(fromStorage)) {
        const parsed = parseISO(fromStorage);
        if (!Number.isNaN(parsed.getTime())) return parsed;
      }
    } catch {}
    return new Date();
  };

  const getInitialShowStats = (): boolean => {
    try {
      const stored = window.localStorage.getItem('workPlansShowStats');
      return stored === 'true';
    } catch {}
    return false;
  };

  // State management
  const [workPlans, setWorkPlans] = useState<WorkPlan[]>([]);
  const [assignedTasks, setAssignedTasks] = useState<WorkPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // View states
  const [viewType, setViewType] = useState<'daily' | 'weekly' | 'monthly'>(getInitialViewType);
  const [displayMode, setDisplayMode] = useState<'calendar' | 'list'>(getInitialDisplayMode);
  const [selectedDate, setSelectedDate] = useState<Date>(getInitialSelectedDate);
  const [showStats, setShowStats] = useState<boolean>(getInitialShowStats);
  const [fabDateDialogOpen, setFabDateDialogOpen] = useState(false);
  const [fabPickDate, setFabPickDate] = useState<Date>(() => getInitialSelectedDate());

  // Inline editing states
  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [newRows, setNewRows] = useState<Set<string>>(new Set());
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set());
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map());
  const [localWorkPlans, setLocalWorkPlans] = useState<WorkPlan[]>([]);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [workPlanToDelete, setWorkPlanToDelete] = useState<string | null>(null);

  const pendingDeleteTaskName = useMemo(() => {
    if (!workPlanToDelete) return '';
    const p = localWorkPlans.find((x) => x.id === workPlanToDelete);
    return p?.taskName?.trim() || '';
  }, [workPlanToDelete, localWorkPlans]);

  // Filter states
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(getInitialStatusFilters);
  const [selectedPriorities, setSelectedPriorities] = useState<Set<string>>(getInitialPriorityFilters);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter helper functions
  const toggleStatusFilter = (status: string) => {
    setSelectedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const togglePriorityFilter = (priority: string) => {
    setSelectedPriorities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(priority)) {
        newSet.delete(priority);
      } else {
        newSet.add(priority);
      }
      return newSet;
    });
  };

  // Get filtered work plans based on selected filters (includes both regular work plans and assigned tasks)
  const getFilteredWorkPlans = useCallback((plans: WorkPlan[]): WorkPlan[] => {
    const allPlans = [...plans, ...assignedTasks];

    return allPlans.filter((plan) => {
      if (selectedStatuses.size > 0 && !selectedStatuses.has(plan.status)) {
        return false;
      }
      if (selectedPriorities.size > 0 && !selectedPriorities.has(plan.priority)) {
        return false;
      }
      return true;
    });
  }, [assignedTasks, selectedStatuses, selectedPriorities]);

  const listViewSortedPlans = useMemo(() => {
    const allFilteredPlans = getFilteredWorkPlans([...localWorkPlans]);
    const draggablePlans = allFilteredPlans.filter((plan) => !plan.isProjectTask);
    const projectTasks = allFilteredPlans.filter((plan) => plan.isProjectTask);
    return [...projectTasks, ...draggablePlans];
  }, [localWorkPlans, getFilteredWorkPlans]);

  const listViewPeriodStats = useMemo(
    () => computeTaskStats(listViewSortedPlans),
    [listViewSortedPlans]
  );

  // Load work plans
  const loadWorkPlans = useCallback(async () => {
    if (!token) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let url = getApiUrl(API_ENDPOINTS.WORK_PLANS);
      const params = new URLSearchParams();

      // Add date filters based on view type
      if (viewType === 'daily') {
        params.append('date', format(selectedDate, 'yyyy-MM-dd'));
      } else if (viewType === 'weekly') {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
        params.append('startDate', format(weekStart, 'yyyy-MM-dd'));
        params.append('endDate', format(weekEnd, 'yyyy-MM-dd'));
      } else if (viewType === 'monthly') {
        const monthStart = startOfMonth(selectedDate);
        const monthEnd = endOfMonth(selectedDate);
        params.append('startDate', format(monthStart, 'yyyy-MM-dd'));
        params.append('endDate', format(monthEnd, 'yyyy-MM-dd'));
      }


      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || t('workPlans.messages.loadError'));
      }

      const data = await response.json();
      const plans = data.workPlans || [];
      
      // Initialize order for plans without order (shouldn't happen after migration, but just in case)
      const plansWithOrder = plans.map((plan: WorkPlan, index: number) => ({
        ...plan,
        order: plan.order !== null && plan.order !== undefined ? plan.order : index
      }));
      
      setWorkPlans(plansWithOrder);
      // Preserve new rows when syncing
      setLocalWorkPlans(prev => {
        const newRowsToKeep = prev.filter((p: WorkPlan) => newRows.has(p.id));
        const serverPlans = plansWithOrder.filter((p: WorkPlan) => !newRows.has(p.id));
        return [...newRowsToKeep, ...serverPlans];
      });
    } catch (err: any) {
      console.error('Error loading work plans:', err);
      setError(err.message || t('workPlans.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [token, viewType, selectedDate, t]);

  // Load assigned Projektai tasks
  const loadAssignedTasks = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      let url = getApiUrl(API_ENDPOINTS.WORK_PLANS_ASSIGNED_TASKS);
      const params = new URLSearchParams();

      // Add date filters based on view type (same as work plans)
      if (viewType === 'daily') {
        params.append('date', format(selectedDate, 'yyyy-MM-dd'));
      } else if (viewType === 'weekly') {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
        params.append('startDate', format(weekStart, 'yyyy-MM-dd'));
        params.append('endDate', format(weekEnd, 'yyyy-MM-dd'));
      } else if (viewType === 'monthly') {
        const monthStart = startOfMonth(selectedDate);
        const monthEnd = endOfMonth(selectedDate);
        params.append('startDate', format(monthStart, 'yyyy-MM-dd'));
        params.append('endDate', format(monthEnd, 'yyyy-MM-dd'));
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assigned tasks');
      }

      const data = await response.json();
      const tasks = data.tasks || [];
      setAssignedTasks(tasks);
    } catch (err) {
      console.error('Error loading assigned tasks:', err);
      // Don't show error to user, just log it
    }
  }, [token, viewType, selectedDate]);

  useEffect(() => {
    loadWorkPlans();
    loadAssignedTasks();
  }, [loadWorkPlans, loadAssignedTasks]);

  // Initialize localWorkPlans when workPlans first loads
  useEffect(() => {
    if (workPlans.length > 0 && localWorkPlans.length === 0) {
      setLocalWorkPlans(workPlans);
    }
  }, [workPlans.length]);

  // Persist displayMode to URL params and localStorage
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('workPlansDisplayMode', displayMode);
      window.history.replaceState({}, '', url.toString());
      window.localStorage.setItem('workPlansDisplayMode', displayMode);
    } catch {}
  }, [displayMode]);

  // Persist selectedStatuses to URL params and localStorage
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (selectedStatuses.size > 0) {
        const statusesArray = Array.from(selectedStatuses);
        url.searchParams.set('workPlansStatuses', statusesArray.join(','));
        window.localStorage.setItem('workPlansStatuses', JSON.stringify(statusesArray));
      } else {
        url.searchParams.delete('workPlansStatuses');
        window.localStorage.removeItem('workPlansStatuses');
      }
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }, [selectedStatuses]);

  // Persist selectedPriorities to URL params and localStorage
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (selectedPriorities.size > 0) {
        const prioritiesArray = Array.from(selectedPriorities);
        url.searchParams.set('workPlansPriorities', prioritiesArray.join(','));
        window.localStorage.setItem('workPlansPriorities', JSON.stringify(prioritiesArray));
      } else {
        url.searchParams.delete('workPlansPriorities');
        window.localStorage.removeItem('workPlansPriorities');
      }
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }, [selectedPriorities]);

  // Persist viewType (period) to URL + localStorage
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('workPlansPeriod', viewType);
      window.history.replaceState({}, '', url.toString());
      window.localStorage.setItem('workPlansPeriod', viewType);
    } catch {}
  }, [viewType]);

  // Persist selectedDate to URL + localStorage
  useEffect(() => {
    try {
      const ds = format(selectedDate, 'yyyy-MM-dd');
      const url = new URL(window.location.href);
      url.searchParams.set('workPlansDate', ds);
      window.history.replaceState({}, '', url.toString());
      window.localStorage.setItem('workPlansDate', ds);
    } catch {}
  }, [selectedDate]);

  // Persist stats visibility
  useEffect(() => {
    try {
      window.localStorage.setItem('workPlansShowStats', String(showStats));
    } catch {}
  }, [showStats]);

  const handleClearFilters = useCallback(() => {
    setSelectedStatuses(new Set());
    setSelectedPriorities(new Set());
  }, []);

  // Listen for WebSocket task updates to sync Projektai tasks
  useEffect(() => {
    if (!user) return;

    const handleTaskUpdate = (data: any) => {
      // Refresh assigned tasks when a task is updated
      if (data.task && data.action) {
        loadAssignedTasks();
      }
    };

    webSocketService.on('task-updated', handleTaskUpdate);

    return () => {
      webSocketService.off('task-updated', handleTaskUpdate);
    };
  }, [user, loadAssignedTasks]);

  const getPriorityColor = (priority: string) => getPriorityToken(priority).stripe;

  const selectOutlineNone = {
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
  };

  const sxSelectPriority = (priority: string) => {
    const p = getPriorityToken(priority);
    return {
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
      boxSizing: 'border-box',
      height: 24,
      bgcolor: p.chipBg,
      color: p.chipText,
      '& .MuiSvgIcon-root': { color: p.chipText },
      '& .MuiSelect-select': {
        py: 0.25,
        pl: '6px',
        pr: '22px',
        fontSize: '0.75rem',
        color: p.chipText,
      },
      '& .MuiSelect-icon': { right: 2, fontSize: '1.1rem' },
      ...selectOutlineNone,
    };
  };

  const sxSelectStatus = (status: string) => {
    const s = getStatusToken(status);
    return {
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
      boxSizing: 'border-box',
      height: 24,
      bgcolor: s.bg,
      color: s.text,
      '& .MuiSvgIcon-root': { color: s.text },
      '& .MuiSelect-select': {
        py: 0.25,
        pl: '6px',
        pr: '22px',
        fontSize: '0.75rem',
        color: s.text,
      },
      '& .MuiSelect-icon': { right: 2, fontSize: '1.1rem' },
      ...selectOutlineNone,
    };
  };

  // Update work plan order via API
  const updateWorkPlanOrder = async (orders: Array<{ id: string; order: number }>) => {
    if (!token) return;
    
    try {
      const url = getApiUrl(API_ENDPOINTS.WORK_PLANS_REORDER);
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orders })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || t('workPlans.messages.loadError'));
      }

      const data = await response.json();
      return data.workPlans || [];
    } catch (err: any) {
      console.error('Error updating work plan order:', err);
      throw err;
    }
  };

  // Add new row
  const handleAddRow = (dateOverride?: Date) => {
    const targetDate = dateOverride ?? selectedDate;
    const tempId = `temp-${Date.now()}`;
    const maxOrder = localWorkPlans.length > 0 
      ? Math.max(...localWorkPlans.map(p => p.order ?? -1)) + 1 
      : 0;
    const newPlan: WorkPlan = {
      id: tempId,
      userId: user?.id || '',
      category: 'work',
      taskName: '',
      date: format(targetDate, 'yyyy-MM-dd'),
      priority: 'medium',
      status: 'laukia',
      difficulty: null,
      delegate: null,
      order: maxOrder,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const updatedPlans = selectedRowId
      ? (() => {
          const index = localWorkPlans.findIndex(p => p.id === selectedRowId);
          return index >= 0
            ? [...localWorkPlans.slice(0, index + 1), newPlan, ...localWorkPlans.slice(index + 1)]
            : [newPlan, ...localWorkPlans];
        })()
      : [newPlan, ...localWorkPlans];

    setLocalWorkPlans(updatedPlans);
    setNewRows(new Set([...newRows, tempId]));
    setSelectedRowId(tempId);
    setEditingCell({ rowId: tempId, field: 'taskName' });
  };

  // Save cell value
  const handleCellSave = async (rowId: string, field: string, value: any) => {
    const cellKey = `${rowId}-${field}`;
    setSavingCells(new Set([...savingCells, cellKey]));
    setCellErrors(new Map(cellErrors.set(cellKey, '')));

    const plan = localWorkPlans.find(p => p.id === rowId) || assignedTasks.find(p => p.id === rowId);
    if (!plan) {
      setSavingCells(new Set([...savingCells].filter(k => k !== cellKey)));
      return;
    }

    // Handle Projektai tasks differently
    if (plan.isProjectTask && plan.taskId) {
      // For Projektai tasks, only allow status and priority changes
      if (field === 'status') {
        // Map WorkPlan status to Task status and call appropriate endpoint
        if (value === 'pradėta' && (plan.status === 'laukia' || plan.status === 'grąžinta')) {
          // Start task (from waiting or returned status)
          await handleTaskStart(plan.taskId);
        } else if (value === 'baigta' && (plan.status === 'pradėta' || plan.status === 'grąžinta')) {
          // Complete task (from started or returned status)
          // If coming from grąžinta, we need to start it first, then complete it
          if (plan.status === 'grąžinta') {
            await handleTaskStart(plan.taskId);
            // Wait a bit for the status to update
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          await handleTaskComplete(plan.taskId);
        }
        // Refresh assigned tasks to sync status
        await loadAssignedTasks();
        setSavingCells(new Set([...savingCells].filter(k => k !== cellKey)));
        return;
      } else if (field === 'priority') {
        // Update task priority
        try {
          const response = await fetch(getApiUrl(`${API_ENDPOINTS.TASKS}/${plan.taskId}`), {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ priority: value })
          });
          if (response.ok) {
            await loadAssignedTasks();
          }
        } catch (err) {
          console.error('Error updating task priority:', err);
        }
        setSavingCells(new Set([...savingCells].filter(k => k !== cellKey)));
        return;
      }
      // For other fields, don't allow editing (read-only)
      setSavingCells(new Set([...savingCells].filter(k => k !== cellKey)));
      return;
    }

    // Validation - only validate taskName when saving the entire row, not for individual fields
    // Allow users to change Priority/Status even if taskName is empty
    if (field === 'difficulty' && value !== null && (value < 1 || value > 10)) {
      setCellErrors(new Map(cellErrors.set(cellKey, 'Difficulty must be between 1 and 10')));
      setSavingCells(new Set([...savingCells].filter(k => k !== cellKey)));
      return;
    }

    // Optimistic update
    const updatedPlan = { ...plan, [field]: value };
    const updatedPlans = localWorkPlans.map(p => p.id === rowId ? updatedPlan : p);
    setLocalWorkPlans(updatedPlans);

    try {
      const isNew = newRows.has(rowId);
      const url = isNew
        ? getApiUrl(API_ENDPOINTS.WORK_PLANS)
        : getApiUrl(API_ENDPOINTS.WORK_PLAN(rowId));

      const method = isNew ? 'POST' : 'PUT';
      
      // Don't validate required fields - allow users to input freely
      // Backend will handle validation if needed
      const body: any = {
        category: updatedPlan.category || 'work',
        taskName: updatedPlan.taskName || '',
        date: updatedPlan.date,
        priority: updatedPlan.priority,
        status: updatedPlan.status,
        difficulty: updatedPlan.difficulty,
        delegate: updatedPlan.delegate
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Save failed' }));
        throw new Error(errorData.error || 'Save failed');
      }

      const savedPlan = await response.json();
      const finalPlan = savedPlan.workPlan || savedPlan;

      // Replace temp ID with real ID if new
      if (isNew && finalPlan.id && finalPlan.id !== rowId) {
        const finalPlans = updatedPlans.map(p => 
          p.id === rowId ? { ...finalPlan, id: finalPlan.id } : p
        );
        setLocalWorkPlans(finalPlans);
        setWorkPlans(finalPlans);
        setNewRows(new Set([...newRows].filter(id => id !== rowId)));
        // Update selectedRowId if it was the temp row
        if (selectedRowId === rowId) {
          setSelectedRowId(finalPlan.id);
        }
      } else {
        // Update existing plan
        setWorkPlans(updatedPlans);
        if (isNew) {
          setNewRows(new Set([...newRows].filter(id => id !== rowId)));
        }
      }

      // Don't clear editingCell here - let the Select onClose handle it for select fields (priority, status)
      // Category is autocomplete and handles its own closing
      // For other field types, clear it immediately
      const isSelectField = field === 'priority' || field === 'status';
      if (!isSelectField) {
        setEditingCell(null);
      }
    } catch (err: any) {
      console.error('Error saving cell:', err);
      setCellErrors(new Map(cellErrors.set(cellKey, err.message || 'Save failed')));
      // Revert optimistic update
      setLocalWorkPlans(localWorkPlans);
      // Clear editing state on error
      setEditingCell(null);
    } finally {
      setSavingCells(new Set([...savingCells].filter(k => k !== cellKey)));
    }
  };

  // Handle starting a Projektai task
  const handleTaskStart = async (taskId: string) => {
    try {
      const response = await fetch(getApiUrl(`${API_ENDPOINTS.TASKS}/${taskId}/start`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to start task' }));
        throw new Error(errorData.error || 'Failed to start task');
      }

      // Refresh assigned tasks
      await loadAssignedTasks();
    } catch (err) {
      console.error('Error starting task:', err);
      setError(err instanceof Error ? err.message : 'Failed to start task');
    }
  };

  // Handle completing a Projektai task
  const handleTaskComplete = async (taskId: string) => {
    try {
      const response = await fetch(getApiUrl(`${API_ENDPOINTS.TASKS}/${taskId}/complete`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to complete task' }));
        throw new Error(errorData.error || 'Failed to complete task');
      }

      // Refresh assigned tasks
      await loadAssignedTasks();
    } catch (err) {
      console.error('Error completing task:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete task');
    }
  };

  // Delete work plan
  const handleDelete = async () => {
    if (!workPlanToDelete) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.WORK_PLAN(workPlanToDelete)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('workPlans.messages.deleteError'));
      }

      setDeleteConfirmOpen(false);
      const deletedId = workPlanToDelete;
      setWorkPlanToDelete(null);
      setLocalWorkPlans(localWorkPlans.filter(p => p.id !== deletedId));
      setNewRows(new Set([...newRows].filter(id => id !== deletedId)));
      await loadWorkPlans();
    } catch (err: any) {
      console.error('Error deleting work plan:', err);
      setError(err.message || t('workPlans.messages.deleteError'));
    } finally {
      setLoading(false);
    }
  };

  // Get work plans for a specific date
  const getWorkPlansForDate = (date: Date): WorkPlan[] => {
    // Filter localWorkPlans by date first
    const dateFilteredLocalPlans = localWorkPlans.filter(wp => wp.date && isSameDay(new Date(wp.date), date));
    // Filter assignedTasks by date
    const dateFilteredAssignedTasks = assignedTasks.filter(wp => wp.date && isSameDay(new Date(wp.date), date));
    
    // getFilteredWorkPlans merges plans with assignedTasks, but we've already filtered assignedTasks by date
    // So we need to temporarily replace assignedTasks, call getFilteredWorkPlans, then restore
    // OR: manually apply the same filters that getFilteredWorkPlans applies
    const allDateFiltered = [...dateFilteredLocalPlans, ...dateFilteredAssignedTasks];
    
    // Apply status and priority filters (same logic as getFilteredWorkPlans)
    return allDateFiltered.filter(plan => {
      // Filter by status if any statuses are selected
      if (selectedStatuses.size > 0 && !selectedStatuses.has(plan.status)) {
        return false;
      }
      // Filter by priority if any priorities are selected
      if (selectedPriorities.size > 0 && !selectedPriorities.has(plan.priority)) {
        return false;
      }
      return true;
    });
  };

  // Navigate dates
  const handlePrevious = () => {
    if (viewType === 'daily') {
      setSelectedDate(subDays(selectedDate, 1));
    } else if (viewType === 'weekly') {
      setSelectedDate(subWeeks(selectedDate, 1));
    } else {
      setSelectedDate(subMonths(selectedDate, 1));
    }
  };

  const handleNext = () => {
    if (viewType === 'daily') {
      setSelectedDate(addDays(selectedDate, 1));
    } else if (viewType === 'weekly') {
      setSelectedDate(addWeeks(selectedDate, 1));
    } else {
      setSelectedDate(addMonths(selectedDate, 1));
    }
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Convert Lithuanian day names from accusative to nominative case
  const toNominativeDayName = (dayName: string): string => {
    const dayNameMap: { [key: string]: string } = {
      'sekmadienį': 'sekmadienis',
      'pirmadienį': 'pirmadienis',
      'antradienį': 'antradienis',
      'trečiadienį': 'trečiadienis',
      'ketvirtadienį': 'ketvirtadienis',
      'penktadienį': 'penktadienis',
      'šeštadienį': 'šeštadienis'
    };
    return dayNameMap[dayName.toLowerCase()] || dayName;
  };

  // Format the current period display based on view type
  const getCurrentPeriodDisplay = () => {
    if (viewType === 'daily') {
      const formatted = format(selectedDate, 'EEEE, MMMM d, yyyy', { locale: lt });
      // Replace accusative day name with nominative
      const parts = formatted.split(', ');
      if (parts.length > 0) {
        parts[0] = toNominativeDayName(parts[0]);
        return parts.join(', ');
      }
      return formatted;
    } else if (viewType === 'weekly') {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return `${format(weekStart, 'MMM d', { locale: lt })} - ${format(weekEnd, 'MMM d, yyyy', { locale: lt })}`;
    } else {
      return format(selectedDate, 'MMMM yyyy', { locale: lt });
    }
  };

  // Render calendar view
  const renderCalendarView = () => {
    if (viewType === 'daily') {
      const dayPlans = getWorkPlansForDate(selectedDate);
      // Separate Projektai tasks from regular tasks and put Projektai tasks first
      const projectTasks = dayPlans.filter(plan => plan.isProjectTask);
      const regularTasks = dayPlans.filter(plan => !plan.isProjectTask);
      const sortedDayPlans = [...projectTasks, ...regularTasks];
      const priorityOptions = getPriorityOptions();
      
      return (
        <Box>
          {sortedDayPlans.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: '#9CA3AF', py: 4 }}>{t('workPlans.noWorkPlans')}</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {sortedDayPlans.map((plan) => {
                const cellKey = (field: string) => `${plan.id}-${field}`;
                const statusOptions = getStatusOptions(plan.isProjectTask, plan.status);
                const cardBorder = plan.isProjectTask ? CARD_PROJECT : CARD_REGULAR;
                
                return (
                  <Card
                    key={plan.id}
                    sx={{
                      borderRadius: '12px',
                      borderLeft: `4px solid ${getPriorityColor(plan.priority)}`,
                      bgcolor: cardBorder.bg,
                      border: plan.isProjectTask ? '1.5px solid #C7D2FE' : CARD_REGULAR.border,
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <Box sx={{ flex: 1 }}>
                          {/* Task Title - Editable */}
                          {plan.isProjectTask ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {plan.taskName || '-'}
                              </Typography>
                              {plan.project?.title && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    ...PROJECT_BADGE_SX,
                                  }}
                                >
                                  <GridViewIcon sx={{ fontSize: 14 }} />
                                  {plan.project.title}
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <EditableTaskName
                              value={plan.taskName}
                              isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'taskName'}
                              isSaving={savingCells.has(cellKey('taskName'))}
                              onSave={(newValue) => {
                                handleCellSave(plan.id, 'taskName', newValue);
                              }}
                              onCancel={() => setEditingCell(null)}
                              onEdit={() => setEditingCell({ rowId: plan.id, field: 'taskName' })}
                              variant="h6"
                              fullWidth
                              disabled={plan.isProjectTask}
                            />
                          )}
                          
                          {/* Date - Editable */}
                          <Box sx={{ mt: 0.5, mb: 1 }}>
                            {savingCells.has(cellKey('date')) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} />
                                <Typography variant="caption" sx={{ opacity: 0.7, whiteSpace: 'nowrap' }}>
                                  {plan.date ? format(new Date(plan.date), 'yyyy-MM-dd') : '-'}
                                </Typography>
                              </Box>
                            ) : editingCell?.rowId === plan.id && editingCell?.field === 'date' && !plan.isProjectTask ? (
                              <DatePicker
                                value={plan.date ? new Date(plan.date) : null}
                                onChange={(newValue) => {
                                  if (newValue) {
                                    const dateStr = format(newValue, 'yyyy-MM-dd');
                                    const updatedPlan = { ...plan, date: dateStr };
                                    const updatedPlans = localWorkPlans.map(p => p.id === plan.id ? updatedPlan : p);
                                    setLocalWorkPlans(updatedPlans);
                                    handleCellSave(plan.id, 'date', dateStr);
                                    setEditingCell(null);
                                  }
                                }}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    autoFocus: true
                                  }
                                }}
                              />
                            ) : (
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                onClick={() => !plan.isProjectTask && setEditingCell({ rowId: plan.id, field: 'date' })}
                                sx={{ 
                                  cursor: plan.isProjectTask ? 'default' : 'pointer',
                                  whiteSpace: 'nowrap',
                                  '&:hover': { opacity: plan.isProjectTask ? 1 : 0.7 }
                                }}
                              >
                                {plan.date ? format(new Date(plan.date), 'yyyy-MM-dd') : '-'}
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Chips Row */}
                          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {/* Category - Editable */}
                            {savingCells.has(cellKey('category')) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CircularProgress size={14} />
                                <Chip 
                                  label={getCategoryLabel(plan.category)} 
                                  size="small" 
                                  sx={{ opacity: 0.7 }}
                                />
                              </Box>
                            ) : editingCell?.rowId === plan.id && editingCell?.field === 'category' && !plan.isProjectTask ? (
                              <Autocomplete
                                value={plan.category || ''}
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    handleCellSave(plan.id, 'category', newValue);
                                  }
                                }}
                                onBlur={() => setEditingCell(null)}
                                options={Array.from(new Set([...['work', 'personal', 'health', 'learning', 'other', 'project'], ...localWorkPlans.map(p => p.category).filter(Boolean)]))}
                                getOptionLabel={(option) => getCategoryLabel(option)}
                                size="small"
                                sx={{ minWidth: 120 }}
                                autoFocus
                                renderInput={(params) => <TextField {...params} size="small" />}
                              />
                            ) : (
                              <Chip 
                                label={getCategoryLabel(plan.category)} 
                                size="small"
                                onClick={() => !plan.isProjectTask && setEditingCell({ rowId: plan.id, field: 'category' })}
                                sx={{ cursor: plan.isProjectTask ? 'default' : 'pointer' }}
                              />
                            )}
                            
                            {/* Priority - Select Dropdown */}
                            {savingCells.has(cellKey('priority')) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CircularProgress size={14} />
                                <Chip 
                                  label={priorityOptions.find(o => o.value === plan.priority)?.label || plan.priority}
                                  size="small"
                                  sx={{
                                    bgcolor: getPriorityToken(plan.priority).chipBg,
                                    color: getPriorityToken(plan.priority).chipText,
                                    opacity: 0.85,
                                  }}
                                />
                              </Box>
                            ) : (
                              <Select
                                value={plan.priority}
                                onChange={(e) => {
                                  handleCellSave(plan.id, 'priority', e.target.value);
                                }}
                                size="small"
                                sx={sxSelectPriority(plan.priority)}
                              >
                                {priorityOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                            
                            {/* Status - Select Dropdown */}
                            {savingCells.has(cellKey('status')) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CircularProgress size={14} />
                                <Chip 
                                  label={statusOptions.find(o => o.value === plan.status)?.label || plan.status}
                                  size="small"
                                  sx={{
                                    bgcolor: getStatusToken(plan.status).bg,
                                    color: getStatusToken(plan.status).text,
                                    opacity: 0.85,
                                  }}
                                />
                              </Box>
                            ) : (
                              <Select
                                value={plan.status}
                                onChange={(e) => {
                                  handleCellSave(plan.id, 'status', e.target.value);
                                }}
                                size="small"
                                sx={sxSelectStatus(plan.status)}
                              >
                                {statusOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                            
                            {/* Difficulty - Editable */}
                            {savingCells.has(cellKey('difficulty')) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CircularProgress size={14} />
                                <Chip 
                                  label={`${t('workPlans.difficulty')}: ${plan.difficulty || '-'}`} 
                                  size="small"
                                  sx={{ opacity: 0.7 }}
                                />
                              </Box>
                            ) : editingCell?.rowId === plan.id && editingCell?.field === 'difficulty' && !plan.isProjectTask ? (
                              <TextField
                                type="number"
                                value={plan.difficulty || ''}
                                onChange={(e) => {
                                  const updatedPlan = { ...plan, difficulty: e.target.value ? parseInt(e.target.value) : null };
                                  const updatedPlans = localWorkPlans.map(p => p.id === plan.id ? updatedPlan : p);
                                  setLocalWorkPlans(updatedPlans);
                                }}
                                onBlur={() => {
                                  handleCellSave(plan.id, 'difficulty', plan.difficulty);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellSave(plan.id, 'difficulty', plan.difficulty);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                size="small"
                                sx={{ width: 80 }}
                                inputProps={{ min: 1, max: 10 }}
                                autoFocus
                              />
                            ) : (
                              <Chip 
                                label={`${t('workPlans.difficulty')}: ${plan.difficulty || '-'}`} 
                                size="small"
                                onClick={() => !plan.isProjectTask && setEditingCell({ rowId: plan.id, field: 'difficulty' })}
                                sx={{ cursor: plan.isProjectTask ? 'default' : 'pointer' }}
                              />
                            )}
                            
                            {/* Delegate - Editable */}
                            {savingCells.has(cellKey('delegate')) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CircularProgress size={14} />
                                <Chip 
                                  label={`${t('workPlans.delegate')}: ${plan.delegate || '-'}`} 
                                  size="small"
                                  sx={{ opacity: 0.7 }}
                                />
                              </Box>
                            ) : editingCell?.rowId === plan.id && editingCell?.field === 'delegate' && !plan.isProjectTask ? (
                              <TextField
                                value={plan.delegate || ''}
                                onChange={(e) => {
                                  const updatedPlan = { ...plan, delegate: e.target.value };
                                  const updatedPlans = localWorkPlans.map(p => p.id === plan.id ? updatedPlan : p);
                                  setLocalWorkPlans(updatedPlans);
                                }}
                                onBlur={() => {
                                  handleCellSave(plan.id, 'delegate', plan.delegate);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellSave(plan.id, 'delegate', plan.delegate);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                size="small"
                                sx={{ minWidth: 120 }}
                                autoFocus
                              />
                            ) : (
                              <Chip 
                                label={`${t('workPlans.delegate')}: ${plan.delegate || '-'}`} 
                                size="small"
                                onClick={() => !plan.isProjectTask && setEditingCell({ rowId: plan.id, field: 'delegate' })}
                                sx={{ cursor: plan.isProjectTask ? 'default' : 'pointer' }}
                              />
                            )}
                          </Box>
                          
                          {/* Error Messages */}
                          {cellErrors.get(cellKey('taskName')) && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {cellErrors.get(cellKey('taskName'))}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          {!plan.isProjectTask && (
                            <IconButton 
                              onClick={() => {
                                setWorkPlanToDelete(plan.id);
                                setDeleteConfirmOpen(true);
                              }} 
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      );
    } else if (viewType === 'weekly') {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
      const today = new Date();

      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {format(weekStart, 'MMM d', { locale: lt })} - {format(weekEnd, 'MMM d, yyyy', { locale: lt })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {weekDays.map((day, dayIndex) => {
              const dayPlans = getWorkPlansForDate(day);
              const projectTasks = dayPlans.filter((plan) => plan.isProjectTask);
              const regularTasks = dayPlans.filter((plan) => !plan.isProjectTask);
              const sortedPlans = [...projectTasks, ...regularTasks];
              const visible = sortedPlans.slice(0, 4);
              const overflow = sortedPlans.length - visible.length;
              const isTodayCol = isSameDay(day, today);

              return (
                <Box key={day.toISOString()} sx={{ flex: '1 1 calc(14.28% - 16px)', minWidth: 120 }}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      borderBottom: isTodayCol ? `2px solid ${WORK_PLANS_ACCENT}` : undefined,
                    }}
                    onClick={() => {
                      setSelectedDate(day);
                      setViewType('daily');
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: isTodayCol ? WORK_PLANS_ACCENT : 'text.primary',
                          fontWeight: isTodayCol ? 700 : 600,
                        }}
                      >
                        {WEEKDAY_HEADERS_LT[dayIndex]} {format(day, 'd', { locale: lt })}
                      </Typography>
                      {visible.map((plan) => {
                        const stripe = getPriorityColor(plan.priority);
                        const bg = alpha(stripe, 0.13);
                        const sxBox = plan.isProjectTask
                          ? {
                              p: 0.75,
                              mb: 0.75,
                              bgcolor: bg,
                              borderRadius: '5px',
                              fontSize: '11px',
                              border: `1px solid ${alpha(stripe, 0.55)}`,
                              cursor: 'not-allowed',
                            }
                          : {
                              p: 0.75,
                              mb: 0.75,
                              bgcolor: bg,
                              borderRadius: '5px',
                              fontSize: '11px',
                              borderLeft: `2px solid ${stripe}`,
                              cursor: 'not-allowed',
                            };
                        return (
                          <Tooltip key={plan.id} title={t('workPlans.readOnlyViewBadge')}>
                            <Box sx={sxBox} onClick={(e) => e.stopPropagation()}>
                              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.primary' }}>
                                {plan.taskName}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                {t(`workPlans.statuses.${plan.status}`)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {overflow > 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                          {t('workPlans.overflowMore', { count: overflow })}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    } else {
      const monthStart = startOfMonth(selectedDate);
      const monthEnd = endOfMonth(selectedDate);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
      const today = new Date();

      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{format(selectedDate, 'MMMM yyyy', { locale: lt })}</Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', mb: 0.5 }}>
              {WEEKDAY_HEADERS_LT.map((day) => (
                <Box key={day} sx={{ flex: '1 1 14.28%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {calendarDays.map((day) => {
                const dayPlans = getWorkPlansForDate(day);
                const isCurrentMonth = isSameMonth(day, selectedDate);
                const projectTasks = dayPlans.filter((plan) => plan.isProjectTask);
                const regularTasks = dayPlans.filter((plan) => !plan.isProjectTask);
                const sortedPlans = [...projectTasks, ...regularTasks];
                const visible = sortedPlans.slice(0, 3);
                const overflow = sortedPlans.length - visible.length;
                const isTodayCell = isSameDay(day, today) && isCurrentMonth;

                return (
                  <Box key={day.toISOString()} sx={{ flex: '1 1 calc(14.28% - 4px)', minWidth: 0 }}>
                    <Card
                      sx={{
                        minHeight: 72,
                        borderRadius: '6px',
                        bgcolor: isTodayCell ? '#EFF6FF' : isCurrentMonth ? 'background.paper' : 'action.hover',
                        border: isTodayCell ? '1.5px solid #BFDBFE' : undefined,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setSelectedDate(day);
                        setViewType('daily');
                      }}
                    >
                      <CardContent sx={{ p: '4px 6px' }}>
                        <Typography variant="caption" sx={{ opacity: isCurrentMonth ? 1 : 0.5 }}>
                          {format(day, 'd', { locale: lt })}
                        </Typography>
                        {visible.map((plan) => (
                          <Tooltip key={plan.id} title={t('workPlans.readOnlyViewBadge')}>
                            <Box
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                mt: 0.5,
                                p: 0.5,
                                fontSize: '10px',
                                borderRadius: 0.5,
                                borderLeft: `2px solid ${getPriorityColor(plan.priority)}`,
                                bgcolor: alpha(getPriorityColor(plan.priority), 0.12),
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'default',
                              }}
                            >
                              {plan.taskName}
                            </Box>
                          </Tooltip>
                        ))}
                        {overflow > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '10px', color: 'text.secondary' }}>
                            {t('workPlans.overflowMore', { count: overflow })}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      );
    }
  };

  // EditableTaskName component - handles local state to prevent cursor jumping
  interface EditableTaskNameProps {
    value: string;
    isEditing: boolean;
    isSaving: boolean;
    onSave: (value: string) => void;
    onCancel: () => void;
    onEdit?: () => void;
    variant?: 'h6' | 'body2';
    fullWidth?: boolean;
    disabled?: boolean;
    /** Read-mode clamp (mobile card); edit uses multiline when > 1 */
    readModeMaxLines?: 1 | 2 | 3;
  }

  const EditableTaskName: React.FC<EditableTaskNameProps> = ({
    value,
    isEditing,
    isSaving,
    onSave,
    onCancel,
    onEdit,
    variant = 'h6',
    fullWidth = true,
    disabled = false,
    readModeMaxLines = 1,
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync local value when external value changes or editing starts
    useEffect(() => {
      setLocalValue(value);
    }, [value, isEditing]);

    // Focus input when editing starts
    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isEditing]);

    const handleBlur = () => {
      if (localValue !== value) {
        onSave(localValue);
      } else {
        onCancel();
      }
    };

    const multilineEdit = readModeMaxLines > 1;

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (multilineEdit) {
        if (e.key === 'Escape') {
          setLocalValue(value);
          onCancel();
        }
        return;
      }
      if (e.key === 'Enter') {
        if (localValue !== value) {
          onSave(localValue);
        } else {
          onCancel();
        }
      } else if (e.key === 'Escape') {
        setLocalValue(value);
        onCancel();
      }
    };

    if (isSaving) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant={variant} sx={{ opacity: 0.7 }}>
            {value || '-'}
          </Typography>
        </Box>
      );
    }

    if (!isEditing) {
      return (
        <Typography 
          variant={variant}
          onClick={disabled ? undefined : onEdit}
          sx={{ 
            cursor: disabled ? 'default' : 'pointer',
            '&:hover': { opacity: disabled ? 1 : 0.7 },
            ...(readModeMaxLines > 1
              ? {
                  display: '-webkit-box',
                  WebkitLineClamp: readModeMaxLines,
                  WebkitBoxOrient: 'vertical' as const,
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }
              : {}),
          }}
        >
          {value || '-'}
        </Typography>
      );
    }

    return (
      <TextField
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        size="small"
        fullWidth={fullWidth}
        autoFocus
        inputRef={inputRef}
        multiline={multilineEdit}
        minRows={multilineEdit ? 2 : undefined}
        maxRows={multilineEdit ? 4 : undefined}
      />
    );
  };

  // EditableCell component
  interface EditableCellProps {
    rowId: string;
    field: string;
    value: any;
    type: 'text' | 'date' | 'select' | 'number' | 'autocomplete';
    options?: { value: string; label: string }[];
    autocompleteOptions?: string[];
    required?: boolean;
    onSave: (rowId: string, field: string, value: any) => Promise<void>;
    isEditing: boolean;
    onEdit: () => void;
    onCancel: () => void;
    isNew?: boolean;
    isSaving?: boolean;
    error?: string;
    disabled?: boolean;
    isProjectTask?: boolean;
    dateFormat?: string;
    cellSx?: SxProps<Theme>;
    /** Use Box layout for mobile list cards instead of TableCell */
    cellVariant?: 'tableCell' | 'stack';
  }

  const EditableCell: React.FC<EditableCellProps> = ({
    rowId,
    field,
    value,
    type,
    options,
    autocompleteOptions,
    required,
    onSave,
    isEditing,
    onEdit,
    onCancel,
    isNew,
    isSaving,
    error,
    disabled = false,
    isProjectTask = false,
    dateFormat = 'yyyy-MM-dd',
    cellSx,
    cellVariant = 'tableCell',
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);
    const savingRef = useRef(false);

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    useEffect(() => {
      if (isEditing && inputRef.current && (type === 'text' || type === 'number' || type === 'autocomplete')) {
        inputRef.current.focus();
        if (type === 'text' || type === 'number') {
          inputRef.current.select();
        }
      }
    }, [isEditing, type]);

    // Handle click outside to close editing
    useEffect(() => {
      if (!isEditing) return;
      
      // Don't add click-outside handler for select type - let Select handle its own closing
      if (type === 'select') {
        return;
      }

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        
        // Don't close if clicking on:
        // - The editing cell itself
        // - Autocomplete dropdown
        // - DatePicker popup
        // - Any MUI popover/menu/paper components
        if (target.closest('.MuiTableCell-root') ||
            target.closest(`.${WORK_PLAN_EDITABLE_FIELD_CLASS}`) ||
            target.closest('.MuiAutocomplete-root') || 
            target.closest('.MuiAutocomplete-popper') ||
            target.closest('.MuiDatePicker-root') ||
            target.closest('.MuiPopover-root') ||
            target.closest('.MuiMenu-root') ||
            target.closest('.MuiMenu-paper') ||
            target.closest('.MuiPaper-root') ||
            target.closest('.MuiPopper-root')) {
          return;
        }
        
        // Close if clicking outside
        if (localValue !== value) {
          onSave(rowId, field, localValue);
        } else {
          onCancel();
        }
      };

      // Add listener after a small delay to avoid immediate trigger
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }, [isEditing, rowId, field, localValue, value, type, onSave, onCancel]);

    const handleBlur = () => {
      if (localValue !== value) {
        onSave(rowId, field, localValue);
      } else {
        onCancel();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (localValue !== value) {
          onSave(rowId, field, localValue);
        } else {
          onCancel();
        }
      } else if (e.key === 'Escape') {
        setLocalValue(value);
        onCancel();
      }
    };

    if (isSaving) {
      const savingInner = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography
            variant="body2"
            sx={{
              opacity: 0.7,
              ...(type === 'date' ? { whiteSpace: 'nowrap' } : {}),
            }}
          >
            {type === 'date' ? format(new Date(value), dateFormat) : 
             type === 'select' && options ? options.find(o => o.value === value)?.label || value :
             value || '-'}
          </Typography>
        </Box>
      );
      if (cellVariant === 'stack') {
        return (
          <Box
            className={WORK_PLAN_EDITABLE_FIELD_CLASS}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              ...cellSx,
            }}
          >
            {savingInner}
          </Box>
        );
      }
      return (
        <TableCell className={WORK_PLAN_EDITABLE_FIELD_CLASS} sx={cellSx}>
          {savingInner}
        </TableCell>
      );
    }

    if (!isEditing) {
      const cellContent = type === 'date' ? (
             <Typography component="span" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
               {format(new Date(value), dateFormat)}
             </Typography>
           ) :
           type === 'select' && options ? (
             <Chip
               label={options.find(o => o.value === value)?.label || value}
               size="small"
               sx={
                 field === 'priority'
                   ? {
                       maxWidth: '100%',
                       height: 22,
                       bgcolor: getPriorityToken(value).chipBg,
                       color: getPriorityToken(value).chipText,
                       fontWeight: 600,
                       border: '1px solid',
                       borderColor: 'transparent',
                       '& .MuiChip-label': {
                         px: 0.75,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap',
                       },
                     }
                   : field === 'status'
                   ? {
                       maxWidth: '100%',
                       height: 22,
                       bgcolor: getStatusToken(value).bg,
                       color: getStatusToken(value).text,
                       fontWeight: 600,
                       border: `1px solid ${getStatusToken(value).border}`,
                       '& .MuiChip-label': {
                         px: 0.75,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap',
                       },
                     }
                   : {}
               }
             />
           ) :
           type === 'autocomplete' && field === 'category' ? (
             <Chip
               label={getCategoryLabel(value)}
               size="small"
               sx={{
                 maxWidth: '100%',
                 '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' },
               }}
             />
           ) :
           type === 'number' ? (
             <Typography variant="body2" component="span" sx={{ color: value ? '#047857' : 'text.secondary', fontWeight: value ? 600 : 400 }}>
               {value || '—'}
             </Typography>
           ) :
           field === 'delegate' ? (
             <Typography
               variant="body2"
               component="span"
               title={value ? String(value) : undefined}
               sx={{
                 color: value ? '#7C3AED' : 'text.secondary',
                 display: 'block',
                 overflow: 'hidden',
                 textOverflow: 'ellipsis',
                 whiteSpace: 'nowrap',
                 maxWidth: '100%',
               }}
             >
               {value || '—'}
             </Typography>
           ) :
           value || (required ? '' : '-');
      
      const readOuterSx = {
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': { bgcolor: disabled ? 'transparent' : ROW_HOVER_BG },
        bgcolor: isNew ? ROW_NEW_BG : 'transparent',
        opacity: disabled && !isProjectTask ? 0.7 : 1,
        ...cellSx,
      };
      if (cellVariant === 'stack') {
        return (
          <Box
            className={WORK_PLAN_EDITABLE_FIELD_CLASS}
            onClick={disabled ? undefined : onEdit}
            sx={readOuterSx}
          >
            {cellContent}
            {error && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {error}
              </Typography>
            )}
          </Box>
        );
      }
      return (
        <TableCell
          className={WORK_PLAN_EDITABLE_FIELD_CLASS}
          onClick={disabled ? undefined : onEdit}
          sx={readOuterSx}
        >
          {cellContent}
          {error && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {error}
            </Typography>
          )}
        </TableCell>
      );
    }

    const editPaddingSx = { py: 1, pl: '6px', pr: '6px', ...cellSx };

    const editingInner = (
      <>
        {type === 'date' ? (
          <DatePicker
            value={localValue ? new Date(localValue) : null}
            onChange={(newValue) => {
              if (newValue) {
                const formatted = format(newValue, 'yyyy-MM-dd');
                setLocalValue(formatted);
                onSave(rowId, field, formatted);
              } else {
                onCancel();
              }
            }}
            onClose={() => {
              if (localValue !== value) {
                onSave(rowId, field, localValue);
              } else {
                onCancel();
              }
            }}
            slotProps={{ textField: { size: 'small', fullWidth: true, autoFocus: true } }}
          />
        ) : type === 'select' && options ? (
          <Select
            value={localValue || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              setLocalValue(newValue);
              if (!savingRef.current) {
                savingRef.current = true;
                onSave(rowId, field, newValue).finally(() => {
                  savingRef.current = false;
                });
              }
            }}
            onClose={() => {
              if (localValue === value && !savingRef.current) {
                savingRef.current = true;
                onSave(rowId, field, localValue).finally(() => {
                  savingRef.current = false;
                });
              }
              setTimeout(() => {
                onCancel();
              }, 300);
            }}
            size="small"
            fullWidth
            autoFocus
            open={isEditing}
            sx={
              field === 'priority'
                ? sxSelectPriority(String(localValue || value || 'medium'))
                : field === 'status'
                ? sxSelectStatus(String(localValue || value || 'laukia'))
                : undefined
            }
            MenuProps={{
              disableAutoFocusItem: true,
              onClick: (e) => {
                e.stopPropagation();
              }
            }}
          >
            {options.map(opt => (
              <MenuItem 
                key={opt.value} 
                value={opt.value}
                onClick={(e) => {
                  // Ensure click propagates to Select's onChange
                  e.stopPropagation();
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        ) : type === 'autocomplete' && autocompleteOptions ? (
          <Autocomplete
            freeSolo
            options={autocompleteOptions}
            value={localValue || ''}
            onChange={(_, newValue) => {
              const finalValue = typeof newValue === 'string' ? newValue : newValue || '';
              setLocalValue(finalValue);
              if (finalValue !== value) {
                onSave(rowId, field, finalValue);
              } else {
                onCancel();
              }
            }}
            onBlur={() => {
              if (localValue !== value) {
                onSave(rowId, field, localValue);
              } else {
                onCancel();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (localValue !== value) {
                  onSave(rowId, field, localValue);
                } else {
                  onCancel();
                }
              } else if (e.key === 'Escape') {
                setLocalValue(value);
                onCancel();
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                autoFocus
                inputRef={inputRef}
                value={localValue || ''}
                onChange={(e) => setLocalValue(e.target.value)}
              />
            )}
          />
        ) : type === 'number' ? (
          <TextField
            type="number"
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value ? parseInt(e.target.value) : null)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 10 }}
          />
        ) : (
          <TextField
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            size="small"
            fullWidth
            required={required}
          />
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </>
    );

    if (cellVariant === 'stack') {
      return (
        <Box className={WORK_PLAN_EDITABLE_FIELD_CLASS} sx={{ ...editPaddingSx, minWidth: 0, width: '100%' }}>
          {editingInner}
        </Box>
      );
    }
    return (
      <TableCell className={WORK_PLAN_EDITABLE_FIELD_CLASS} sx={editPaddingSx}>
        {editingInner}
      </TableCell>
    );
  };

  const getPriorityOptions = () => [
    { value: 'low', label: t('workPlans.priorities.low') },
    { value: 'medium', label: t('workPlans.priorities.medium') },
    { value: 'high', label: t('workPlans.priorities.high') },
    { value: 'extreme', label: t('workPlans.priorities.extreme') },
    { value: 'ultra', label: t('workPlans.priorities.ultra') }
  ];

  const getStatusOptions = (forProjectTask: boolean = false, currentStatus?: string) => {
    const allOptions = [
      { value: 'laukia', label: t('workPlans.statuses.laukia') },
      { value: 'pradėta', label: t('workPlans.statuses.pradėta') },
      { value: 'baigta', label: t('workPlans.statuses.baigta') },
      { value: 'atšaukta', label: t('workPlans.statuses.atšaukta') },
      { value: 'perduota', label: t('workPlans.statuses.perduota') },
      { value: 'grąžinta', label: t('workPlans.statuses.grąžinta') }
    ];
    
    if (forProjectTask) {
      // For Projektai tasks, allow "Pradėta" and "Baigta"
      // Also include "grąžinta" if it's the current status (so Select doesn't complain)
      const allowedOptions = allOptions.filter(opt => 
        opt.value === 'pradėta' || 
        opt.value === 'baigta' || 
        (opt.value === 'grąžinta' && currentStatus === 'grąžinta')
      );
      return allowedOptions;
    }
    
    // For regular work plans (Darbų planai): do not offer "Grąžinta" manually — only set from Projektai when task is returned/denied
    // Include "grąžinta" when it's the current status so the Select displays correctly and doesn't break
    return allOptions.filter(opt =>
      opt.value !== 'grąžinta' || currentStatus === 'grąžinta'
    );
  };

  // Sortable Row Component
  interface SortableRowProps {
    plan: WorkPlan;
    isSelected: boolean;
    isNew: boolean;
    cellKey: (field: string) => string;
    onRowClick: () => void;
  }

  const SortableRow: React.FC<SortableRowProps> = ({ plan, isSelected, isNew, cellKey, onRowClick }) => {
    const priorityOptions = getPriorityOptions();
    const statusOptions = getStatusOptions(plan.isProjectTask, plan.status);
    const actionsCellBg = plan.isProjectTask
      ? isSelected
        ? ROW_SELECTED_BG
        : isNew
        ? ROW_NEW_BG
        : CARD_PROJECT.bg
      : isSelected
      ? ROW_SELECTED_BG
      : isNew
      ? ROW_NEW_BG
      : '#ffffff';
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: plan.id,
      disabled: plan.isProjectTask || editingCell !== null,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      cursor: plan.isProjectTask || editingCell !== null ? 'default' : 'grab',
      ...(isDragging && { zIndex: 1000 })
    };

    return (
      <TableRow
        ref={setNodeRef}
        style={style}
        onClick={onRowClick}
        sx={{
          bgcolor: plan.isProjectTask
            ? isSelected
              ? ROW_SELECTED_BG
              : isNew
              ? ROW_NEW_BG
              : CARD_PROJECT.bg
            : isSelected
            ? ROW_SELECTED_BG
            : isNew
            ? ROW_NEW_BG
            : 'transparent',
          borderBottom: '1px solid #F0F4F8',
          border: plan.isProjectTask ? '1.5px solid #C7D2FE' : undefined,
          '&:hover': { bgcolor: editingCell === null ? ROW_HOVER_BG : undefined },
          cursor: plan.isProjectTask ? 'pointer' : editingCell === null ? 'grab' : 'pointer',
          ...(isDragging && {
            boxShadow: 3,
            bgcolor: ROW_SELECTED_BG,
          }),
        }}
      >
        <TableCell sx={{ width: 32, px: 0.5, verticalAlign: 'middle', borderBottom: '1px solid #F0F4F8' }}>
          {plan.isProjectTask ? (
            <Box
              sx={{
                width: 4,
                minHeight: 28,
                borderRadius: 1,
                bgcolor: getPriorityColor(plan.priority),
                mx: 'auto',
              }}
            />
          ) : (
            <IconButton
              size="small"
              {...attributes}
              {...listeners}
              disabled={editingCell !== null}
              sx={{
                cursor: editingCell === null ? 'grab' : 'default',
                p: 0.25,
                '&:active': {
                  cursor: editingCell === null ? 'grabbing' : 'default',
                },
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
        <EditableCell
          rowId={plan.id}
          field="date"
          value={plan.date}
          type="date"
          dateFormat="MM-dd"
          onSave={handleCellSave}
          isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'date'}
          onEdit={() => !plan.isProjectTask && setEditingCell({ rowId: plan.id, field: 'date' })}
          onCancel={() => setEditingCell(null)}
          isNew={isNew}
          isSaving={savingCells.has(cellKey('date'))}
          error={cellErrors.get(cellKey('date'))}
          disabled={plan.isProjectTask}
          isProjectTask={plan.isProjectTask}
          cellSx={{
            width: 56,
            maxWidth: 56,
            whiteSpace: 'nowrap',
            borderBottom: '1px solid #F0F4F8',
          }}
        />
        <TableCell
          sx={{
            bgcolor: isNew ? ROW_NEW_BG : 'transparent',
            position: { xs: 'sticky', sm: 'static' },
            left: { xs: 88, sm: 'auto' },
            zIndex: { xs: 2, sm: 'auto' },
            boxShadow: { xs: '4px 0 8px rgba(15,23,42,0.06)', sm: 'none' },
            minWidth: { xs: 200, sm: 240 },
            maxWidth: { xs: 280, sm: 340 },
            width: '28%',
            borderBottom: '1px solid #F0F4F8',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
            {plan.isProjectTask && plan.project && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ...PROJECT_BADGE_SX,
                }}
              >
                <GridViewIcon sx={{ fontSize: 14 }} />
                {plan.project.title}
              </Box>
            )}
            {plan.isProjectTask ? (
              <Typography variant="body2">
                {plan.taskName || ''}
              </Typography>
            ) : (
              <EditableTaskName
                value={plan.taskName}
                isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'taskName'}
                isSaving={savingCells.has(cellKey('taskName'))}
                onSave={(newValue) => {
                  handleCellSave(plan.id, 'taskName', newValue);
                }}
                onCancel={() => setEditingCell(null)}
                onEdit={() => setEditingCell({ rowId: plan.id, field: 'taskName' })}
                variant="body2"
                fullWidth
                disabled={plan.isProjectTask}
              />
            )}
          </Box>
          {cellErrors.get(cellKey('taskName')) && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {cellErrors.get(cellKey('taskName'))}
            </Typography>
          )}
        </TableCell>
        <EditableCell
          rowId={plan.id}
          field="status"
          value={plan.status}
          type="select"
          options={statusOptions}
          onSave={handleCellSave}
          isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'status'}
          onEdit={() => setEditingCell({ rowId: plan.id, field: 'status' })}
          onCancel={() => setEditingCell(null)}
          isNew={isNew}
          isSaving={savingCells.has(cellKey('status'))}
          error={cellErrors.get(cellKey('status'))}
          cellSx={{
            width: 104,
            maxWidth: 104,
            overflow: 'hidden',
            borderBottom: '1px solid #F0F4F8',
          }}
        />
        <EditableCell
          rowId={plan.id}
          field="priority"
          value={plan.priority}
          type="select"
          options={priorityOptions}
          onSave={handleCellSave}
          isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'priority'}
          onEdit={() => setEditingCell({ rowId: plan.id, field: 'priority' })}
          onCancel={() => setEditingCell(null)}
          isNew={isNew}
          isSaving={savingCells.has(cellKey('priority'))}
          error={cellErrors.get(cellKey('priority'))}
          cellSx={{
            width: 104,
            maxWidth: 104,
            overflow: 'hidden',
            borderBottom: '1px solid #F0F4F8',
          }}
        />
        <EditableCell
          rowId={plan.id}
          field="category"
          value={plan.category}
          type="autocomplete"
          autocompleteOptions={Array.from(new Set([...['work', 'personal', 'health', 'learning', 'other'], ...localWorkPlans.map(p => p.category).filter(Boolean)]))}
          onSave={handleCellSave}
          isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'category'}
          onEdit={() => !plan.isProjectTask && setEditingCell({ rowId: plan.id, field: 'category' })}
          onCancel={() => setEditingCell(null)}
          isNew={isNew}
          isSaving={savingCells.has(cellKey('category'))}
          error={cellErrors.get(cellKey('category'))}
          disabled={plan.isProjectTask}
          isProjectTask={plan.isProjectTask}
          cellSx={{
            width: 120,
            maxWidth: 120,
            overflow: 'hidden',
            borderBottom: '1px solid #F0F4F8',
          }}
        />
        <EditableCell
          rowId={plan.id}
          field="difficulty"
          value={plan.difficulty?.toString() || ''}
          type="number"
          onSave={handleCellSave}
          isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'difficulty'}
          onEdit={() => setEditingCell({ rowId: plan.id, field: 'difficulty' })}
          onCancel={() => setEditingCell(null)}
          isNew={isNew}
          isSaving={savingCells.has(cellKey('difficulty'))}
          error={cellErrors.get(cellKey('difficulty'))}
          cellSx={{
            width: 56,
            maxWidth: 56,
            overflow: 'hidden',
            borderBottom: '1px solid #F0F4F8',
            pl: 2,
          }}
        />
        <EditableCell
          rowId={plan.id}
          field="delegate"
          value={plan.delegate || ''}
          type="text"
          onSave={handleCellSave}
          isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'delegate'}
          onEdit={() => setEditingCell({ rowId: plan.id, field: 'delegate' })}
          onCancel={() => setEditingCell(null)}
          isNew={isNew}
          isSaving={savingCells.has(cellKey('delegate'))}
          error={cellErrors.get(cellKey('delegate'))}
          cellSx={{
            width: 120,
            maxWidth: 120,
            overflow: 'hidden',
            borderBottom: '1px solid #F0F4F8',
          }}
        />
        <TableCell
          sx={{
            position: 'sticky',
            right: 0,
            zIndex: 4,
            bgcolor: actionsCellBg,
            borderBottom: '1px solid #F0F4F8',
            borderLeft: '1px solid #E8EDF5',
            boxShadow: '-8px 0 16px rgba(15, 23, 42, 0.06)',
            width: 52,
            minWidth: 52,
            maxWidth: 52,
            textAlign: 'center',
            verticalAlign: 'middle',
            px: 0.5,
          }}
        >
          {!plan.isProjectTask && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setWorkPlanToDelete(plan.id);
                setDeleteConfirmOpen(true);
              }}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    );
  };

  // Render list view
  const renderListView = () => {
    const sortedPlans = listViewSortedPlans;
    const draggablePlans = sortedPlans.filter((plan) => !plan.isProjectTask);

    // Handle drag end event - only for regular work plans
    const handleDragEnd = async (event: DragEndEvent) => {
      const { active, over } = event;
      
      if (!over || active.id === over.id) {
        return;
      }

      // Only allow dragging regular work plans (not Projektai tasks)
      const activePlan = draggablePlans.find((plan: WorkPlan) => plan.id === active.id);
      const overPlan = draggablePlans.find((plan: WorkPlan) => plan.id === over.id);
      
      if (!activePlan || !overPlan || activePlan.isProjectTask || overPlan.isProjectTask) {
        return;
      }

      const oldIndex = draggablePlans.findIndex((plan: WorkPlan) => plan.id === active.id);
      const newIndex = draggablePlans.findIndex((plan: WorkPlan) => plan.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Don't allow dragging while editing
      if (editingCell !== null) {
        return;
      }

      // Reorder only the draggable plans
      const reorderedPlans = arrayMove(draggablePlans, oldIndex, newIndex);
      
      // Calculate new orders (only for regular work plans)
      const orders = reorderedPlans.map((plan: WorkPlan, index: number) => ({
        id: plan.id,
        order: index
      }));

      // Optimistically update local state (only regular work plans)
      setLocalWorkPlans(reorderedPlans.map((plan: WorkPlan, index: number) => ({
        ...plan,
        order: index
      })));

      // Update order in backend
      try {
        const updatedPlans = await updateWorkPlanOrder(orders);
        // Sync with server response
        setLocalWorkPlans((prev: WorkPlan[]) => {
          const updatedMap = new Map<string, WorkPlan>(updatedPlans.map((p: WorkPlan) => [p.id, p]));
          return prev.map((p: WorkPlan): WorkPlan => {
            const updated = updatedMap.get(p.id);
            return updated || p;
          });
        });
      } catch (err: any) {
        // Rollback on error
        setError(err.message || t('workPlans.messages.loadError'));
        // Reload plans to restore correct order
        loadWorkPlans();
      }
    };

    const metaCellSxMobile = {
      flex: '0 1 auto' as const,
      maxWidth: 152,
      minWidth: 0,
      borderBottom: 'none',
    };

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {isMobileListLayout ? (
          <Paper sx={{ borderRadius: 2, border: '1px solid #E8EDF5', p: 1.5, bgcolor: '#fff' }}>
            {sortedPlans.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                {t('workPlans.noWorkPlans')}
              </Typography>
            ) : (
              <SortableContext items={sortedPlans.map((p: WorkPlan) => p.id)} strategy={verticalListSortingStrategy}>
                <Stack spacing={1.25}>
                  {sortedPlans.map((plan) => {
                    const isSelected = selectedRowId === plan.id;
                    const isNew = newRows.has(plan.id);
                    const cellKey = (field: string) => `${plan.id}-${field}`;
                    const priorityOptions = getPriorityOptions();
                    const statusOptions = getStatusOptions(plan.isProjectTask, plan.status);
                    return (
                      <SortableMobileCardFrame
                        key={plan.id}
                        plan={plan}
                        isSelected={isSelected}
                        isNew={isNew}
                        editingLocked={editingCell !== null}
                        onRowClick={() => setSelectedRowId(plan.id)}
                      >
                        {({ attributes, listeners }) => (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                              <Box sx={{ flexShrink: 0, width: 32, display: 'flex', justifyContent: 'center', pt: 0.25 }}>
                                {plan.isProjectTask ? (
                                  <Box
                                    sx={{
                                      width: 4,
                                      minHeight: 28,
                                      borderRadius: 1,
                                      bgcolor: getPriorityColor(plan.priority),
                                      mx: 'auto',
                                    }}
                                  />
                                ) : (
                                  <IconButton
                                    size="small"
                                    {...attributes}
                                    {...listeners}
                                    disabled={editingCell !== null}
                                    sx={{
                                      cursor: editingCell === null ? 'grab' : 'default',
                                      p: 0.25,
                                      '&:active': {
                                        cursor: editingCell === null ? 'grabbing' : 'default',
                                      },
                                    }}
                                  >
                                    <DragIndicatorIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              <EditableCell
                                cellVariant="stack"
                                rowId={plan.id}
                                field="date"
                                value={plan.date}
                                type="date"
                                dateFormat="MM-dd"
                                onSave={handleCellSave}
                                isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'date'}
                                onEdit={() => !plan.isProjectTask && setEditingCell({ rowId: plan.id, field: 'date' })}
                                onCancel={() => setEditingCell(null)}
                                isNew={isNew}
                                isSaving={savingCells.has(cellKey('date'))}
                                error={cellErrors.get(cellKey('date'))}
                                disabled={plan.isProjectTask}
                                isProjectTask={plan.isProjectTask}
                                cellSx={{
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap',
                                  borderBottom: 'none',
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {plan.isProjectTask && plan.project && (
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        alignSelf: 'flex-start',
                                        ...PROJECT_BADGE_SX,
                                      }}
                                    >
                                      <GridViewIcon sx={{ fontSize: 14 }} />
                                      {plan.project.title}
                                    </Box>
                                  )}
                                  {plan.isProjectTask ? (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical' as const,
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {plan.taskName || ''}
                                    </Typography>
                                  ) : (
                                    <EditableTaskName
                                      value={plan.taskName}
                                      isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'taskName'}
                                      isSaving={savingCells.has(cellKey('taskName'))}
                                      onSave={(newValue) => {
                                        handleCellSave(plan.id, 'taskName', newValue);
                                      }}
                                      onCancel={() => setEditingCell(null)}
                                      onEdit={() => setEditingCell({ rowId: plan.id, field: 'taskName' })}
                                      variant="body2"
                                      fullWidth
                                      disabled={plan.isProjectTask}
                                      readModeMaxLines={3}
                                    />
                                  )}
                                </Box>
                                {cellErrors.get(cellKey('taskName')) && (
                                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                    {cellErrors.get(cellKey('taskName'))}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ flexShrink: 0 }}>
                                {!plan.isProjectTask && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setWorkPlanToDelete(plan.id);
                                      setDeleteConfirmOpen(true);
                                    }}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                mt: 1,
                                alignItems: 'center',
                                rowGap: 0.75,
                              }}
                            >
                              <EditableCell
                                cellVariant="stack"
                                rowId={plan.id}
                                field="status"
                                value={plan.status}
                                type="select"
                                options={statusOptions}
                                onSave={handleCellSave}
                                isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'status'}
                                onEdit={() => setEditingCell({ rowId: plan.id, field: 'status' })}
                                onCancel={() => setEditingCell(null)}
                                isNew={isNew}
                                isSaving={savingCells.has(cellKey('status'))}
                                error={cellErrors.get(cellKey('status'))}
                                cellSx={metaCellSxMobile}
                              />
                              <EditableCell
                                cellVariant="stack"
                                rowId={plan.id}
                                field="priority"
                                value={plan.priority}
                                type="select"
                                options={priorityOptions}
                                onSave={handleCellSave}
                                isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'priority'}
                                onEdit={() => setEditingCell({ rowId: plan.id, field: 'priority' })}
                                onCancel={() => setEditingCell(null)}
                                isNew={isNew}
                                isSaving={savingCells.has(cellKey('priority'))}
                                error={cellErrors.get(cellKey('priority'))}
                                cellSx={metaCellSxMobile}
                              />
                              <EditableCell
                                cellVariant="stack"
                                rowId={plan.id}
                                field="category"
                                value={plan.category}
                                type="autocomplete"
                                autocompleteOptions={Array.from(
                                  new Set([
                                    ...['work', 'personal', 'health', 'learning', 'other'],
                                    ...localWorkPlans.map((p) => p.category).filter(Boolean),
                                  ])
                                )}
                                onSave={handleCellSave}
                                isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'category'}
                                onEdit={() => !plan.isProjectTask && setEditingCell({ rowId: plan.id, field: 'category' })}
                                onCancel={() => setEditingCell(null)}
                                isNew={isNew}
                                isSaving={savingCells.has(cellKey('category'))}
                                error={cellErrors.get(cellKey('category'))}
                                disabled={plan.isProjectTask}
                                isProjectTask={plan.isProjectTask}
                                cellSx={metaCellSxMobile}
                              />
                              <EditableCell
                                cellVariant="stack"
                                rowId={plan.id}
                                field="difficulty"
                                value={plan.difficulty?.toString() || ''}
                                type="number"
                                onSave={handleCellSave}
                                isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'difficulty'}
                                onEdit={() => setEditingCell({ rowId: plan.id, field: 'difficulty' })}
                                onCancel={() => setEditingCell(null)}
                                isNew={isNew}
                                isSaving={savingCells.has(cellKey('difficulty'))}
                                error={cellErrors.get(cellKey('difficulty'))}
                                cellSx={{
                                  ...metaCellSxMobile,
                                  maxWidth: 88,
                                  minWidth: 52,
                                  minHeight: 40,
                                  py: 0.75,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  boxSizing: 'border-box',
                                  ml: 1,
                                }}
                              />
                              <EditableCell
                                cellVariant="stack"
                                rowId={plan.id}
                                field="delegate"
                                value={plan.delegate || ''}
                                type="text"
                                onSave={handleCellSave}
                                isEditing={editingCell?.rowId === plan.id && editingCell?.field === 'delegate'}
                                onEdit={() => setEditingCell({ rowId: plan.id, field: 'delegate' })}
                                onCancel={() => setEditingCell(null)}
                                isNew={isNew}
                                isSaving={savingCells.has(cellKey('delegate'))}
                                error={cellErrors.get(cellKey('delegate'))}
                                cellSx={{
                                  ...metaCellSxMobile,
                                  maxWidth: 180,
                                  minWidth: 120,
                                  minHeight: 40,
                                  py: 0.75,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  boxSizing: 'border-box',
                                }}
                              />
                            </Box>
                          </>
                        )}
                      </SortableMobileCardFrame>
                    );
                  })}
                </Stack>
              </SortableContext>
            )}
          </Paper>
        ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, border: '1px solid #E8EDF5' }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': {
                pl: '6px',
                pr: '6px',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 32, bgcolor: '#F8FAFC', borderBottom: '1px solid #E8EDF5' }} />
                <TableCell
                  sx={{
                    width: 56,
                    maxWidth: 56,
                    bgcolor: '#F8FAFC',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #E8EDF5',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t('workPlans.date')}
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#F8FAFC',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #E8EDF5',
                    minWidth: 240,
                    maxWidth: 340,
                    width: '28%',
                  }}
                >
                  {t('workPlans.taskName')}
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#F8FAFC',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #E8EDF5',
                    width: 104,
                    maxWidth: 104,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t('workPlans.status')}
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#F8FAFC',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #E8EDF5',
                    width: 104,
                    maxWidth: 104,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t('workPlans.priority')}
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#F8FAFC',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #E8EDF5',
                    width: 120,
                    maxWidth: 120,
                    overflow: 'hidden',
                  }}
                >
                  {t('workPlans.category')}
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#F8FAFC',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #E8EDF5',
                    width: 56,
                    maxWidth: 56,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    pl: 2,
                  }}
                >
                  {t('workPlans.shortDifficulty')}
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#F8FAFC',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #E8EDF5',
                    width: 120,
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t('workPlans.shortDelegate')}
                </TableCell>
                <TableCell
                  scope="col"
                  aria-label={t('common.actions')}
                  sx={{
                    position: 'sticky',
                    right: 0,
                    top: 0,
                    zIndex: 6,
                    bgcolor: '#F8FAFC',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #E8EDF5',
                    borderLeft: '1px solid #E8EDF5',
                    boxShadow: '-8px 0 16px rgba(15, 23, 42, 0.06)',
                    width: 52,
                    minWidth: 52,
                    maxWidth: 52,
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                />
              </TableRow>
            </TableHead>
            <SortableContext
              items={sortedPlans.map((p: WorkPlan) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {sortedPlans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {t('workPlans.noWorkPlans')}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedPlans.map((plan) => {
                    const isSelected = selectedRowId === plan.id;
                    const isNew = newRows.has(plan.id);
                    const cellKey = (field: string) => `${plan.id}-${field}`;

                    return (
                      <SortableRow
                        key={plan.id}
                        plan={plan}
                        isSelected={isSelected}
                        isNew={isNew}
                        cellKey={cellKey}
                        onRowClick={() => setSelectedRowId(plan.id)}
                      />
                    );
                  })
                )}
              </TableBody>
            </SortableContext>
          </Table>
        </TableContainer>
        )}
      </DndContext>
    );
  };

  const handleFabClick = () => {
    if (viewType === 'weekly' || viewType === 'monthly') {
      setFabPickDate(selectedDate);
      setFabDateDialogOpen(true);
    } else {
      handleAddRow();
    }
  };

  const handleFabDateConfirm = () => {
    setFabDateDialogOpen(false);
    setSelectedDate(fabPickDate);
    handleAddRow(fabPickDate);
  };

  const filterChipActiveCount = selectedStatuses.size + selectedPriorities.size;

  const tabSx = {
    textTransform: 'none' as const,
    minHeight: 42,
    fontWeight: 500,
    color: '#6B7280',
    '&.Mui-selected': {
      color: WORK_PLANS_ACCENT,
      fontWeight: 600,
    },
  };

  return (
    <ThemeProvider theme={workPlansNestedTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
        <Box sx={{ minHeight: '100%', bgcolor: '#FAFBFC' }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 50,
              bgcolor: '#ffffff',
              borderBottom: '1px solid #E8EDF5',
            }}
          >
            <Box
              sx={{
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 1.5, sm: 2 },
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{t('workPlans.title')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={t('workPlans.statsToggleTooltip')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setShowStats((s) => !s)}
                      color={showStats ? 'primary' : 'default'}
                      aria-label={t('workPlans.statsToggle')}
                      aria-pressed={showStats}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={displayMode}
                  onChange={(_, v) => v != null && setDisplayMode(v)}
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 1.5,
                      textTransform: 'none',
                      borderRadius: '8px',
                      border: '1.5px solid #E5E7EB',
                      color: '#6B7280',
                      '&.Mui-selected': {
                        bgcolor: WORK_PLANS_ACCENT,
                        color: '#fff',
                        border: 'none',
                        '&:hover': { bgcolor: WORK_PLANS_ACCENT },
                      },
                    },
                  }}
                >
                  <ToggleButton value="list">
                    <ListIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    {t('workPlans.displayModes.list')}
                  </ToggleButton>
                  <ToggleButton value="calendar">
                    <CalendarIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    {t('workPlans.displayModes.calendar')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            <Box sx={{ borderTop: '1px solid #F0F4F8' }}>
              <Tabs
                value={viewType}
                onChange={(_, v) => setViewType(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  px: 1,
                  '& .MuiTabs-indicator': { backgroundColor: WORK_PLANS_ACCENT, height: 2 },
                }}
              >
                <Tab label={t('workPlans.views.daily')} value="daily" sx={tabSx} />
                <Tab label={t('workPlans.views.weekly')} value="weekly" sx={tabSx} />
                <Tab label={t('workPlans.views.monthly')} value="monthly" sx={tabSx} />
              </Tabs>
            </Box>
          </Box>

          <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: '14px', sm: '20px' }, py: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <IconButton
                onClick={handlePrevious}
                aria-label={
                  viewType === 'daily'
                    ? t('workPlans.navigation.previousDay')
                    : viewType === 'weekly'
                    ? t('workPlans.navigation.previousWeek')
                    : t('workPlans.navigation.previousMonth')
                }
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="subtitle1" sx={{ flex: 1, textAlign: 'center', fontWeight: 600, color: '#111827' }}>
                {getCurrentPeriodDisplay()}
              </Typography>
              <Button onClick={handleToday} size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                {t('common.today', 'Šiandien')}
              </Button>
              <IconButton
                onClick={handleNext}
                aria-label={
                  viewType === 'daily'
                    ? t('workPlans.navigation.nextDay')
                    : viewType === 'weekly'
                    ? t('workPlans.navigation.nextWeek')
                    : t('workPlans.navigation.nextMonth')
                }
              >
                <ChevronRight />
              </IconButton>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 0.75, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                {t('workPlans.status')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {WORK_PLAN_STATUSES.map((status) => (
                  <WorkPlansStatusChip
                    key={status}
                    status={status}
                    label={t(`workPlans.statuses.${status}`)}
                    onClick={() => toggleStatusFilter(status)}
                    filterInactive={!selectedStatuses.has(status)}
                    filterActive={selectedStatuses.has(status)}
                  />
                ))}
              </Box>
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 0.75, mt: 1.5, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                {t('workPlans.priority')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {WORK_PLAN_PRIORITIES.map((priority) => (
                  <WorkPlansPriorityChip
                    key={priority}
                    priority={priority}
                    label={t(`workPlans.priorities.${priority}`)}
                    onClick={() => togglePriorityFilter(priority)}
                    filterInactive={!selectedPriorities.has(priority)}
                    filterActive={selectedPriorities.has(priority)}
                    showDot
                  />
                ))}
                {filterChipActiveCount > 0 && (
                  <Chip
                    size="small"
                    label={t('workPlans.clearFilters')}
                    onClick={handleClearFilters}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: '#FEF2F2',
                      color: '#DC2626',
                      border: '1.5px solid #FCA5A5',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            </Box>

            {showStats && displayMode === 'list' && !loading && (
              <Box
                component="section"
                aria-label={t('workPlans.stats.title')}
                sx={{
                  mb: 2,
                  p: '14px 18px',
                  bgcolor: '#F8FAFC',
                  borderRadius: '12px',
                  border: '1px solid #E8EDF5',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {t('workPlans.stats.title')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                    <Typography variant="subtitle2" component="span" sx={{ mr: 0.5 }}>
                      {t('workPlans.status')}:
                    </Typography>
                    {WORK_PLAN_STATUSES.map((status) => {
                      const st = getStatusToken(status);
                      return (
                        <Chip
                          key={status}
                          size="small"
                          variant="filled"
                          label={`${t(`workPlans.statuses.${status}`)}: ${listViewPeriodStats.statusCounts[status]}`}
                          sx={{
                            bgcolor: st.bg,
                            color: st.text,
                            border: `1px solid ${st.border}`,
                            fontWeight: 600,
                          }}
                        />
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                    <Typography variant="subtitle2" component="span" sx={{ mr: 0.5 }}>
                      {t('workPlans.priority')}:
                    </Typography>
                    {WORK_PLAN_PRIORITIES.map((priority) => {
                      const pt = getPriorityToken(priority);
                      return (
                        <Chip
                          key={priority}
                          size="small"
                          variant="filled"
                          label={`${t(`workPlans.priorities.${priority}`)}: ${listViewPeriodStats.priorityCounts[priority]}`}
                          sx={{
                            bgcolor: pt.chipBg,
                            color: pt.chipText,
                            fontWeight: 600,
                          }}
                        />
                      );
                    })}
                  </Box>
                  <Typography variant="body2" sx={{ color: '#111827' }}>
                    {t('workPlans.difficulty')}:{' '}
                    {listViewPeriodStats.averageDifficulty !== null ? (
                      <Box component="span" sx={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>
                        {t('workPlans.stats.avgDifficulty', {
                          value: String(listViewPeriodStats.averageDifficulty.toFixed(1)),
                        })}
                      </Box>
                    ) : (
                      t('workPlans.stats.noDifficultyData')
                    )}
                  </Typography>
                </Box>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : displayMode === 'calendar' ? (
              renderCalendarView()
            ) : (
              renderListView()
            )}
          </Box>

          <Fab
            color="primary"
            aria-label={t('workPlans.createWorkPlan')}
            sx={{
              position: 'fixed',
              right: 20,
              bottom: 20,
              zIndex: 100,
              width: 52,
              height: 52,
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
              '&:hover': { transform: 'scale(1.08)', boxShadow: '0 6px 20px rgba(0,0,0,0.24)' },
            }}
            onClick={handleFabClick}
          >
            <AddIcon sx={{ fontSize: 24 }} />
          </Fab>

          <Dialog open={fabDateDialogOpen} onClose={() => setFabDateDialogOpen(false)}>
            <DialogTitle>{t('workPlans.fabPickDateTitle')}</DialogTitle>
            <DialogContent>
              <DatePicker value={fabPickDate} onChange={(v) => v && setFabPickDate(v)} slotProps={{ textField: { size: 'small', fullWidth: true, sx: { mt: 1 } } }} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setFabDateDialogOpen(false)}>{t('workPlans.cancel')}</Button>
              <Button variant="contained" onClick={handleFabDateConfirm}>
                {t('workPlans.fabPickDateConfirm')}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
            slotProps={{
              backdrop: {
                sx: {
                  bgcolor: 'rgba(0,0,0,0.35)',
                  backdropFilter: 'blur(4px)',
                },
              },
            }}
            PaperProps={{
              sx: { borderRadius: '16px', px: 1, py: 0.5 },
            }}
          >
            <DialogTitle>{t('workPlans.deleteWorkPlan')}</DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Typography>
                {pendingDeleteTaskName
                  ? t('workPlans.messages.confirmDeleteNamed', { taskName: pendingDeleteTaskName })
                  : t('workPlans.messages.confirmDelete')}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
                {t('workPlans.cancel')}
              </Button>
              <Button onClick={handleDelete} variant="contained" color="error" disabled={loading}>
                {t('workPlans.delete')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default WorkPlans;

